import api from "@/lib/api";

/* Lehká paměťová cache (TTL 20 s) — zrychlí přepínání mezi CRM stránkami: klienti/cíle se
   nenačítají znovu při každém kroku. Invaliduje se při jakékoli změně (create/update/delete). */
const TTL = 20000;
const _cache = {};
const cached = (key, fetcher) => {
  const e = _cache[key];
  if (e && Date.now() - e.ts < TTL) return Promise.resolve(e.data);
  return fetcher().then((data) => { _cache[key] = { data, ts: Date.now() }; return data; });
};
const bust = (...keys) => keys.forEach((k) => { delete _cache[k]; });
export const invalidateCrmCache = () => { for (const k in _cache) delete _cache[k]; };

// Contacts
export const listContacts = (opts = {}) => { if (opts.force) bust("contacts"); return cached("contacts", () => api.get("/api/crm/contacts").then(r => r.data)); };
export const getContact = (id) => api.get(`/api/crm/contacts/${id}`).then(r => r.data);
export const createContact = (data) => api.post("/api/crm/contacts", data).then(r => { bust("contacts"); return r.data; });
export const updateContact = (id, data) => api.put(`/api/crm/contacts/${id}`, data).then(r => { bust("contacts"); return r.data; });
export const deleteContact = (id) => api.delete(`/api/crm/contacts/${id}`).then(r => { bust("contacts"); return r.data; });

// Koš (soft delete) — deleteContact výše je nově SOFT (archived=true), tady je zbytek životního cyklu
export const listTrash = () => api.get("/api/crm/contacts/trash").then(r => r.data);
export const restoreContact = (id) => api.post(`/api/crm/contacts/${id}/restore`).then(r => { bust("contacts"); return r.data; });
export const hardDeleteContact = (id) => api.delete(`/api/crm/contacts/${id}/hard`).then(r => r.data);

// Goals
export const getGoals = () => cached("goals", () => api.get("/api/crm/goals").then(r => r.data));
export const updateGoals = (data) => api.put("/api/crm/goals", data).then(r => { bust("goals"); return r.data; });

// Tasks
export const upcomingFixations = (days = 120) => api.get("/api/contracts/upcoming-fixations", { params: { days } }).then(r => r.data);
export const listTasks = () => api.get("/api/crm/tasks").then(r => r.data);
export const createTask = (data) => api.post("/api/crm/tasks", data).then(r => r.data);
export const updateTask = (id, data) => api.put(`/api/crm/tasks/${id}`, data).then(r => r.data);
export const deleteTask = (id) => api.delete(`/api/crm/tasks/${id}`).then(r => r.data);

// IK Sessions (individuální konzultace manažer × poradce)
// Bez advisorId = „moje IK" (co o mně napsal manažer). S advisorId = pohled
// manažera na konkrétního poradce; backend hlídá potvrzený vztah.
export const listIkSessions = (advisorId) =>
  api.get("/api/crm/ik", { params: advisorId ? { advisor_id: advisorId } : undefined }).then(r => r.data);
export const createIkSession = (data) => api.post("/api/crm/ik", data).then(r => r.data);
export const updateIkSession = (id, data) => api.put(`/api/crm/ik/${id}`, data).then(r => r.data);
export const deleteIkSession = (id) => api.delete(`/api/crm/ik/${id}`).then(r => r.data);
export const getIkAdvisorSnapshot = (advisorId) =>
  api.get("/api/crm/ik/advisor-snapshot", { params: { advisor_id: advisorId } }).then(r => r.data);
export const listIkTeam = () => api.get("/api/crm/team").then(r => r.data);

// Auth — password change
export const changePassword = (old_password, new_password) =>
  api.put("/api/auth/change-password", { old_password, new_password }).then(r => r.data);

// Finanční mapa (analýza klienta)
export const getFinMap = (cid) => api.get(`/api/crm/contacts/${cid}/finmap`).then((r) => r.data);
export const saveFinMap = (cid, data) => api.put(`/api/crm/contacts/${cid}/finmap`, { data }).then((r) => r.data);

// Volné poznámky ke klientovi (markdown) — ukládají se ke klientovi, čte je AI.
export const listNotes = (cid) => api.get(`/api/notes`, { params: { contact_id: cid } }).then((r) => r.data);
export const createNote = (cid, content) => api.post(`/api/notes`, { contact_id: cid, content }).then((r) => r.data);
export const deleteNote = (id) => api.delete(`/api/notes/${id}`).then((r) => r.data);

/* Můj vztah k manažerovi + čekající pozvánky do týmu.
 * Self-scoped a read-only (backend: routes/manager.my_relationship) — nemá
 * parametr, kterým by šlo zjistit něco o někom jiném.
 * Čte to zvoneček, aby poradci pozvánka mlčky nevypršela. */
export const myRelationship = () =>
  api.get("/api/manager/my-relationship", { timeout: 12000 }).then((r) => r.data);

/* Záznam, že se stávajícímu členovi týmu JEDNOU ukázala věta „co vidí tvůj
 * manažer" (zvoneček, components/notif/informaceNotif.js). Self-scoped: server
 * zapíše verzi věty jen na MŮJ potvrzený vztah a nikdy podruhé. */
export const potvrditInformaciManazera = (verze) =>
  api.post("/api/manager/my-relationship/informace", { informace_verze: verze }, { timeout: 12000 }).then((r) => r.data);
